import { IChartConfig } from './chart-config.interface';
import { DefaultColorFactory } from './default-color-factory';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { IColorFactory } from './color-factory.interface';
import { IChartDrawer } from './chart-drawer.interface';
import { isUndefined } from 'lodash';

export class BarChartDrawer implements IChartDrawer {
  private readonly groupGapCoefficient = 1;
  private readonly barGapCoefficient = 0.25;
  private readonly additionalLeftPadding: number;
  private readonly minBarWidth = 15;

  private readonly _height: number = 350;
  private readonly _width: number;
  private readonly _offset: number;
  private readonly _ticksCount: number = 5;
  private readonly _tickStep: number;
  private readonly _paddingLeft: number = 50;
  private readonly _paddingTop: number = 25;
  private readonly _paddingBottom: number = 25;
  private readonly _barWidth: number;
  private readonly _groupGap: number;
  private readonly _barGap: number;
  private readonly _chartMax: number;
  private readonly _rotateLabels: boolean;
  private readonly _linesMode: boolean;
  private readonly _colorFactory: IColorFactory;
  private readonly _data: number[][];
  private readonly _labels: string[];
  private readonly _labelWidth: number;
  private readonly _tickLabel: string;
  private readonly _hideZeroBar: boolean = false;
  private readonly _isOverflowed: boolean;
  private readonly _maxOffset: number;

  constructor(chartConfig: IChartConfig, private readonly domSanitizer: DomSanitizer) {
    this._isOverflowed = false;
    this._maxOffset = 0;

    this._data = chartConfig.data;

    this._labels = chartConfig.labels ?? [];

    this._tickLabel = chartConfig.tickLabel ?? '';

    this._hideZeroBar = chartConfig.hideZeroBar;

    this._colorFactory = chartConfig.colorFactory ?? new DefaultColorFactory();

    if (chartConfig.height) {
      this._height = chartConfig.height;
    }

    this._linesMode = chartConfig.linesMode;
    this._width = chartConfig.width;
    this.additionalLeftPadding = isUndefined(chartConfig.additionalLeftPadding)
      ? 50
      : chartConfig.additionalLeftPadding;
    this._paddingLeft = isUndefined(chartConfig.paddingLeft) ? 50 : chartConfig.paddingLeft;
    this._offset = chartConfig.offset;

    const totalBars = this._linesMode ? this._data.length : this._data.reduce((sum, x) => sum + x.length, 0);
    const totalGroups = this._data.length;

    this._rotateLabels = totalGroups > 10 && !chartConfig.disableRotateLabels;

    if (this._rotateLabels) {
      this._paddingLeft += this.additionalLeftPadding;
    }

    this._barWidth =
      (this._width - this._paddingLeft) /
      (totalBars * (1 + this.barGapCoefficient) + totalGroups * (this.groupGapCoefficient - this.barGapCoefficient));

    if (this._barWidth < this.minBarWidth) {
      this._barWidth = this.minBarWidth;
      this._isOverflowed = true;
      this._maxOffset =
        this._barWidth *
          (totalBars * (1 + this.barGapCoefficient) +
            totalGroups * (this.groupGapCoefficient - this.barGapCoefficient)) +
        this._paddingLeft -
        this._width;
    }

    this._groupGap = this._barWidth * this.groupGapCoefficient;

    this._barGap = this._barWidth * this.barGapCoefficient;

    this._labelWidth = (this._width + this._maxOffset - this._paddingLeft) / totalGroups;

    const max = Math.max(chartConfig.alternativeMax ?? 0, Math.max(...this._data.map((x) => Math.max(...x))));

    this._tickStep = max === 0 ? 1 : Math.ceil(max / 5);

    if (chartConfig.tickStep) {
      this._tickStep = chartConfig.tickStep;
      this._ticksCount = Math.ceil(max) / chartConfig.tickStep;
    }

    this._chartMax = this._tickStep * this._ticksCount;
  }

  public getIsOverflowed(): boolean {
    return this._isOverflowed;
  }

  public getMinOffset(): number {
    return -this._maxOffset;
  }

  public get(): SafeHtml {
    let sb = '';

    sb += `<div style="width: ${this._width}px">`;

    sb += `<svg viewBox="0 0 ${this._width} ${this._height}" width="${this._width}px" height="${this._height}px">`;

    sb += this.getAxis();

    sb += `<defs>
             <clipPath id="clip-chart">
               <polygon points="${this._paddingLeft - this._offset} 0, 99999 0, 99999 ${this._height}, ${
      this._paddingLeft - this._offset
    } ${this._height}" />
             </clipPath>
           </defs>`;

    sb += `<g overflow="hidden" transform="translate(${this._offset} 0)" clip-path="url(#clip-chart)">`;

    if (this._linesMode) {
      sb += this.getFirstBars();
      sb += this.getRestLines();
    } else {
      sb += this.getBars();
    }

    sb += '</g>';

    sb += '</svg>';

    if (this._labels.length) {
      sb += this.getLabels();
    }

    sb += '</div>';

    return this.domSanitizer.bypassSecurityTrustHtml(sb);
  }

  private getAxis(): string {
    let sb = '';

    sb += '<g>';

    sb += `<path class="stroke-path" d="M ${this._paddingLeft} ${this._height - this._paddingBottom} H ${
      this._width
    }" fill="none" stroke="#b1b1b1"></path>`;
    sb += `<text class="axis-text" text-anchor="end" dy="0.32em" transform="translate(${this._paddingLeft} ${
      this._height - this._paddingBottom
    })">0${this._tickLabel}&nbsp;</text>`;

    for (let i = 1; i <= this._ticksCount; i++) {
      sb += `<path class="stroke-path" d="M ${this._paddingLeft} ${
        ((this._height - this._paddingBottom - this._paddingTop) / this._ticksCount) * (this._ticksCount - i) +
        this._paddingTop
      } H ${this._width}" fill="none" stroke-dasharray="2" stroke="#b1b1b1"></path>`;
      sb += `<text class="axis-text" text-anchor="end" dy="0.32em" transform="translate(${this._paddingLeft} ${
        ((this._height - this._paddingBottom - this._paddingTop) / this._ticksCount) * (this._ticksCount - i) +
        this._paddingTop
      })">${this._tickStep * i}${this._tickLabel}&nbsp;</text>`;
    }

    sb += '</g>';

    return sb;
  }

  private getBars(): string {
    let sb = '';

    sb += '<g>';

    let groupIndex = 0;
    let barIndex = 0;

    for (const group of this._data) {
      sb += '<g>';

      let barIndexInGroup = 0;

      for (const bar of group) {
        sb += `<path d="${this.getBarPath(bar, barIndex, groupIndex)}" class="${this._colorFactory.get(
          barIndex,
          barIndexInGroup,
          bar
        )} tooltip-item" fill="#9B9B9B"></path>`;

        barIndex++;
        barIndexInGroup++;
      }

      sb += '</g>';

      groupIndex++;
    }

    sb += '</g>';

    return sb;
  }

  private getFirstBars(): string {
    let sb = '';

    sb += '<g>';

    let groupIndex = 0;
    let barIndex = 0;

    for (const group of this._data) {
      const bar = group[0];

      sb += '<g>';

      // display set to none if we need to hide bar. make it like that because of tooltips functionality
      sb += `<path d="${this.getBarPath(bar, groupIndex, groupIndex)}" class="${this._colorFactory.get(
        barIndex,
        0,
        bar
      )} tooltip-item"
              style="display: ${!this._hideZeroBar || bar ? 'block' : 'none'}" fill="#9B9B9B"></path>`;

      barIndex += group.length;

      sb += '</g>';

      groupIndex++;
    }

    sb += '</g>';

    return sb;
  }

  private getRestLines(): string {
    let sb = '';

    sb += '<g>';

    for (let lineIndex = 1; lineIndex < this._data[0]?.length; lineIndex++) {
      const values = this._data.map((x) => x[lineIndex]);

      sb += `<path d="${this.getLinePath(values, lineIndex - 1)}" class="comparison-line ${this._colorFactory.get(
        0,
        lineIndex,
        0
      )}" stroke-width="2"></path>`;
    }

    sb += '</g>';

    return sb;
  }

  private getBarPath(rawValue: number, barIndex: number, groupIndex: number): string {
    const value = 1 - Math.max(rawValue / this._chartMax, 0.01);

    let sb = '';

    const x1 =
      this._paddingLeft +
      this._groupGap / 2 +
      this._groupGap * groupIndex +
      this._barWidth * barIndex +
      this._barGap * (barIndex - groupIndex);

    const y1 = this._height - this._paddingBottom;

    sb += `M ${x1} ${y1}`;

    const x2 = x1;
    const y2 = (this._height - this._paddingBottom - this._paddingTop) * value + this._paddingTop;

    sb += `V ${y2}`;

    const x3 = x2 + this._barWidth;
    sb += `H ${x3}`;

    const y4 = y1;

    sb += `V ${y4}`;

    sb += 'Z';

    return sb;
  }

  private getLinePath(values: number[], groupIndex: number): string {
    let sb = '';

    if (values.length === 1) {
      const value = 1 - values[0] / this._chartMax;
      const lineShift = 4;

      const x = this._paddingLeft + this._groupGap / 2;

      const y =
        (this._height - this._paddingBottom - this._paddingTop - groupIndex * lineShift) * value + this._paddingTop;

      sb = `M ${x} ${y} L ${x + this._barWidth} ${y}`;
    } else {
      let index = 0;

      for (const rawValue of values) {
        const value = 1 - rawValue / this._chartMax;
        const lineShift = 2;

        const x =
          this._paddingLeft + this._groupGap / 2 + this._groupGap * index + this._barWidth * index + this._barWidth / 2;

        const y =
          (this._height - this._paddingBottom - this._paddingTop - groupIndex * lineShift) * value + this._paddingTop;

        sb += `L ${x} ${y}`;

        index++;
      }

      if (sb.length > 0) {
        sb = `M${sb.substr(1)}`;
      }
    }

    return sb;
  }

  private getLabels(): string {
    let sb = '';

    sb += `<div class="bar-chart-labels" style="padding: 0 0 0 ${
      this._paddingLeft
    }px; display: block; position: relative; top: -${this._paddingBottom}px; height: ${
      this._rotateLabels ? 115 : 50
    }px; font-size: 0; text-align: left; overflow: hidden; white-space: nowrap;">`;

    for (const label of this._labels) {
      sb += this._rotateLabels
        ? `<div class="bar-chart-label" style="width: ${this._labelWidth}px; display: inline-block; text-align: right; position: relative; overflow: visible; left: ${this._offset}px"><div style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; width: 141px; transform: rotate(-45deg); -webkit-transform: rotate(-45deg); font-size: 16px; transform-origin: right; -webkit-transform-origin: right; display: inline-block; position: absolute; right: 50%;">${label}</div></div>`
        : `<div class="bar-chart-label" style="width: ${this._labelWidth}px; display: inline-block; text-align: center; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 16px; padding: 0 10px; position: relative; left: ${this._offset}px">${label}</div>`;
    }

    sb += '</div>';

    return sb;
  }
}
